import { useEffect, useRef } from "react";

import { Cross, Tick } from "components/icons/generated";
import ListEntitiesItem from "components/ListEntitiesItem";
import ListEntitiesItemLink from "components/ListEntitiesItem/Link";
import Box from "ds/components/Box";
import { useDrawerVisibilityForId } from "ds/components/DrawerNew/useDrawerVisibilityForId";
import Icon from "ds/components/Icon";
import Typography from "ds/components/Typography";
import { AccessResult } from "utils/access";
import { REFETCH_STACK_SETTINGS_QUERIES } from "views/Stack/Settings/constants";
import Timestamp from "components/Timestamp";

import StackSettingsIntegrationsWebhooksDropdown from "../Dropdown";
import { COLUMN_ORDER } from "./constants";
import { WebhookIntegrationWithTimestamp } from "./types";

type StackSettingsIntegrationsWebhooksListItemProps = {
  item: WebhookIntegrationWithTimestamp;
  setRowHeight?: (size: number) => void;
  stackId: string;
  onEditClick: (item: WebhookIntegrationWithTimestamp) => void;
  onDetailsClick: (item: WebhookIntegrationWithTimestamp) => void;
  canManageStack: AccessResult;
};

const StackSettingsIntegrationsWebhooksListItem = ({
  item,
  setRowHeight,
  stackId,
  onEditClick,
  onDetailsClick,
  canManageStack,
}: StackSettingsIntegrationsWebhooksListItemProps) => {
  const rowRef = useRef<HTMLDivElement>(null);

  const isActive = useDrawerVisibilityForId(item.id);

  const handleRowHeight = () => {
    if (setRowHeight && rowRef.current) {
      setRowHeight(rowRef.current.getBoundingClientRect().height);
    }
  };

  // on every render, update the row height
  useEffect(handleRowHeight);

  return (
    <ListEntitiesItem
      direction="row"
      align="center"
      justify="between"
      grid
      gridTemplate={COLUMN_ORDER}
      gap="0 large"
      ref={rowRef}
      isActive={isActive}
      role="row"
    >
      <Box role="rowheader" align="center">
        <ListEntitiesItemLink
          href={`/stack/${stackId}/settings/integrations/webhook/${item.id}`}
          title={item.endpoint}
        />
      </Box>

      <Box role="cell" align="center" gap="small">
        {item.enabled ? (
          <>
            <Icon src={Tick} color="success" />
            <Typography tag="span" variant="p-body2" color="success">
              Enabled
            </Typography>
          </>
        ) : (
          <>
            <Icon src={Cross} color="danger" />
            <Typography tag="span" variant="p-body2" color="danger">
              Disabled
            </Typography>
          </>
        )}
      </Box>

      <Box role="cell" align="center">
        <Timestamp timestamp={item.createdAt} variant="p-body2" />
      </Box>

      <Box role="cell" align="end">
        <StackSettingsIntegrationsWebhooksDropdown
          id={item.id}
          endpoint={item.endpoint}
          refetchQueriesOnDelete={REFETCH_STACK_SETTINGS_QUERIES}
          onEditClick={() => onEditClick(item)}
          onDetailsClick={() => onDetailsClick(item)}
          stackId={stackId}
          canManageStack={canManageStack}
        />
      </Box>
    </ListEntitiesItem>
  );
};

export default StackSettingsIntegrationsWebhooksListItem;
